import aliases from '../../../data/inventory_identity_aliases_v1.json';
import trees from '../../../data/tree_size_rules_v1.json';
import { readInventoryField, readLayerListEntryUuid } from './inventory-raw-readers';
import { readItemField } from './item-field-reader';
import { fromBaseClone, fromBaseRecord } from './from-base-context';
import { compileInventoryTreeFromBase, type FromBaseInventoryMetadata, type FromBaseInventoryTree } from './from-base-inventory';
import { createFormulaReadContext } from './formula-read-context';
import { createVitalityReader } from './vitality-reader';
import type { SpawnReaders, SpawnRowPlan } from '../spawn/row-plan';

type Row = Record<string, unknown>;
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const fresco = new Set(['58AB82B7-0EB9-1AD7-7D4D-458F3757E9DD', '3C483890-2650-4546-B089-6372A494CF63', 'AC3772BC-4FF4-C373-252D-7ACB227CEE58']);
const catalogAliases: Record<string, { canonical: string; type: string; default_mesh_id: string | null }> = aliases.aliases;
const treeAliases: Record<string, { itemId: string; sizeId: string }> = trees.aliases;
export function inventoryGrantSourceIds(ids: readonly string[]): string[] {
    return [...new Set(ids.flatMap(id => {
        const key = upper(id), target = treeAliases[key]?.itemId ?? (fresco.has(key) ? '86C31E94-809A-FD22-CFD0-C4E0ADFFB784' : catalogAliases[key]?.canonical);
        return target ? [id, target] : [id];
    }))];
}

/** The source instance is constructed by the same native tree compiler as the inventory page.
 * This is a proposed new row, so one sibling has a known order and empty saved relations. */
export function compileInventoryGrantInstance(row: Row, base: Row, metadata: FromBaseInventoryMetadata, possessor?: Row): Row {
    const holder = upper(readInventoryField({ inventory: row }, 'possess_id'));
    const node = { inventory: row, base_rev: 0, statuses: { target: [], caster: [] }, charges: [], pictures: [],
        inventory_key_links: [], inventory_lock_bindings: [], inventory_closure_states: [], inventory_encounter_creature_links: [] };
    const catalog = fromBaseRecord(base.catalog) ? base.catalog as Record<string, Row> : {};
    const definition = catalog[upper(readInventoryField(node, 'inventory_item_id'))];
    const governing = possessor ?? (fromBaseRecord(base.possessor) ? base.possessor : {});
    const rawPossessor = Object.fromEntries(['encounter_creatures', 'player_characters', 'encounters', 'locations', 'inventory']
        .filter(key => fromBaseRecord(governing[key])).map(key => [key, governing[key]]));
    const input: Row = { ...base, entity_id: holder, base_rev: 0, inventory: [node], metadata,
        inventory_order: [{ ...row, item_name: readItemField(definition ?? null, 'item_name') }],
        encounter_links: [], picture_lookup_ids: [], picture_sources: [], possessor: rawPossessor };
    // Explicit presentation pointers refer to already selected definition media in a spawn package.
    const pointers = ['inventory_presentation_pic_id', 'inventory_thumbnail_pic_id'].map(key => readInventoryField(node, key))
        .filter((id): id is string => typeof id === 'string' && id !== '');
    const pictures = Object.values(catalog).flatMap(item => Array.isArray(item.pictures) ? item.pictures as Row[] : []);
    input.picture_lookup_ids = [...new Set(pointers)];
    input.picture_sources = pictures.filter(pic => pointers.includes(String(readItemField(pic, 'picture_id'))));
    const result = compileInventoryTreeFromBase(input as unknown as FromBaseInventoryTree, catalog);
    if (result.missingStages.length || !result.inventory?.[0]) throw new Error(`inventory-grant:inputs:${result.missingStages.join(',')}:${result.detail ?? ''}`);
    return result.inventory[0];
}

/** New-grant identity only. Saved instances never pass through this planner. Explicit zero wins. */
export function planInventoryIdentityGrant(row: Row, nativeSource: Row, catalog: Readonly<Record<string, Row>>,
    metadata: FromBaseInventoryMetadata): Row {
    const sourceId = upper(readInventoryField({ inventory: row }, 'inventory_item_id'));
    const tree = treeAliases[sourceId], spec = catalogAliases[sourceId];
    const targetId = tree?.itemId ?? (fresco.has(sourceId) ? '86C31E94-809A-FD22-CFD0-C4E0ADFFB784' : spec?.canonical);
    if (!targetId) return fromBaseClone(row);
    const source = catalog[sourceId], target = catalog[upper(targetId)];
    if (!source) throw new Error(`inventory-grant:identity-source:${sourceId}`);
    // A manifest can precede its installed transaction. Keep the source until both rows and the anchor moved.
    if (spec && (Number(readItemField(source, 'item_archived') ?? 0) !== 1 || !target
        || Number(readItemField(target, 'item_archived') ?? 0) !== 0)) return fromBaseClone(row);
    if (!target) throw new Error(`inventory-grant:identity-source:${targetId}`);
    const meshes = Array.isArray(target.meshes) ? target.meshes as Row[] : [];
    const anchor = spec?.default_mesh_id ? meshes.find(mesh => upper(readItemField(mesh, 'mesh_id')) === spec.default_mesh_id
        && upper(readItemField(mesh, 'mesh_attached_id')) === upper(targetId)) : null;
    if (spec?.default_mesh_id && !anchor) return fromBaseClone(row);
    const expected = tree ? 'tree' : fresco.has(sourceId) ? 'artwork' : spec!.type;
    if (readItemField(source, 'item_type') !== expected || readItemField(target, 'item_type') !== expected)
        throw new Error('Inventory identity alias type drift');
    const readers = createFormulaReadContext({ catalogRows: metadata.layerCatalog, abilityTypes: metadata.abilityTypes,
        aggregationStrategy: name => metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: id => catalog[upper(id)] ?? null, resolveInventoryInstance: () => null });
    const out = fromBaseClone(row), fill = (key: string, value: unknown): void => {
        const before = readInventoryField({ inventory: out }, key);
        if (before === null || before === '') out[key] = value;
    };
    out.inventory_item_id = targetId;
    fill('inventory_size', tree?.sizeId ?? readers.layerValue(nativeSource, 'size'));
    fill('weight_override', readers.layerValue(nativeSource, 'weight'));
    if (!tree) {
        const cost = fromBaseRecord(nativeSource.computed) && fromBaseRecord(nativeSource.computed.cost) ? nativeSource.computed.cost : null;
        fill('inventory_cost_gp', Number(readItemField(cost, 'total_gp') ?? 0) / Math.max(1, Number(readInventoryField({ inventory: row }, 'item_qty') ?? 1)));
    }
    if (spec) {
        if (Number(readInventoryField({ inventory: row }, 'v_max') ?? 0) <= 0) {
            const vitality = createVitalityReader({ resolveInventoryInstance: () => null })(nativeSource);
            if (Number(readItemField(vitality, 'v_max') ?? 0) > 0) {
                out.v_max = readItemField(vitality, 'v_max'); out.v_current = readItemField(vitality, 'v_current');
            }
        }
        if (anchor && !Number(readItemField(anchor, 'mesh_deleted') ?? 0) && !Number(readItemField(anchor, 'mesh_unapproved') ?? 0))
            fill('mesh_id', spec.default_mesh_id);

    }
    return out;
}

/** Member rows remain authored data. Existing spell/charge members survive the size-passive union. */
export function composeInventorySizeMembers(user: readonly Row[], sizing: readonly Row[]): Row[] {
    const out = user.map(row => fromBaseClone(row)), passives = new Set(user.filter(row => readItemField(row, 'enchantment_type') === 'passive_ability')
        .map(row => upper(readLayerListEntryUuid(readItemField(row, 'enchantment_item_id')))));
    for (const row of sizing) {
        if (readItemField(row, 'enchantment_type') !== 'passive_ability') continue;
        const id = upper(readLayerListEntryUuid(readItemField(row, 'enchantment_item_id')));
        if (id && !passives.has(id)) { passives.add(id); out.push(fromBaseClone(row)); }
    }
    return out;
}

/** Decorate the existing native spawn planner; children and receipts keep their original plan. */
export function inventoryIdentitySpawnReaders(read: SpawnReaders, base: Row, metadata: FromBaseInventoryMetadata): SpawnReaders {
    const catalog = fromBaseRecord(base.catalog) ? base.catalog as Record<string, Row> : {};
    return { ...read, inventory(...args): SpawnRowPlan {
        const plan = read.inventory(...args);
        for (const write of plan.writes) if (write.table === 'inventory' && write.op === 'insert') {
            const id = upper(readInventoryField({ inventory: write.row }, 'inventory_item_id'));
            if (treeAliases[id] || fresco.has(id) || catalogAliases[id])
                write.row = planInventoryIdentityGrant(write.row, compileInventoryGrantInstance(treeAliases[id] ? { ...write.row, inventory_size: null } : write.row, base, metadata, args[1]), catalog, metadata);
        }
        return plan;
    } };
}

/** Native authoring proposal for a user template plus creation-time sizing.
 * The caller persists `authoring` through the existing enchantment authoring save,
 * then uses templateId in its typed inventory proposal. No template is saved here. */
export function planInventorySizeEnchantment(existingId: string, sizeId: string, base: Row, newId: () => string):
    { templateId: string; authoring?: { schema: 'enchantment-authoring.v1'; kind: 'template'; template_id: string; name: string;
        members: Array<{ kind: 'member'; type: string; value: string }> } } {
    if (!sizeId || upper(existingId) === upper(sizeId)) return { templateId: existingId || sizeId };
    if (!existingId) return { templateId: sizeId };
    const rows = Array.isArray(base.enchantment_template_enchantments) ? base.enchantment_template_enchantments as Row[] : [];
    const templates = (Array.isArray(base.enchantment_templates) ? base.enchantment_templates as Row[] : [])
        .filter(row => !Number(readItemField(row, 'enchantment_template_deleted') ?? 0));
    const members = (id: string): Row[] => rows.filter(row => upper(readItemField(row, 'enchantment_template_id')) === upper(id)
        && !Number(readItemField(row, 'enchantment_template_enchantment_deleted') ?? 0));
    for (const id of [existingId, sizeId]) if (!templates.some(row => upper(readItemField(row, 'enchantment_template_id')) === upper(id)))
        throw new Error(`inventory-grant:template-source:${id}`);
    const composed = composeInventorySizeMembers(members(existingId), members(sizeId));
    const identity = (entries: readonly Row[]): string => JSON.stringify(entries.map(row => [
        String(readItemField(row, 'enchantment_type')), String(readItemField(row, 'enchantment_item_id'))]).sort());
    const signature = identity(composed);
    // Prefer the selected identity when it already carries the size passive.
    if (identity(members(existingId)) === signature) return { templateId: existingId };
    const match = templates.find(row => identity(members(String(readItemField(row, 'enchantment_template_id')))) === signature);
    if (match) return { templateId: String(readItemField(match, 'enchantment_template_id')) };
    const templateId = newId();
    return { templateId, authoring: { schema: 'enchantment-authoring.v1', kind: 'template', template_id: templateId, name: '',
        members: composed.map(row => ({ kind: 'member', type: String(readItemField(row, 'enchantment_type')),
            value: String(readItemField(row, 'enchantment_item_id')) })) } };
}
